import type {
  QuoteEventData,
  Route,
  RouteEventData,
  Step,
  StepEventData,
  UiEventData,
  WalletEventData,
} from '@rango-dev/widget-embedded';

import { SOCIAL_WALLET_TYPE } from '@rango-dev/internal-social-wallet';
import {
  QuoteEventTypes,
  RouteEventType,
  StepEventType,
  UiEventTypes,
  WalletEventTypes,
} from '@rango-dev/widget-embedded';

import { pushToDataLayer, toNumberOrNull, toSnakeCaseKeys } from './dataLayer';
import { GA4Events } from './ga4Events';

/**
 * Maps a widget failure reason code to the taxonomy `failure_reason` enum.
 *
 * TODO(analytics): the frontend-owned `failure_reason` enum is still to be
 * finalised in RC-A-53. Until then we forward the SDK reason code (a fixed
 * enum, not a raw exception string), which is a stable, allowed value.
 */
function mapFailureReason(reasonCode: string | null | undefined): string {
  return reasonCode ?? 'unknown';
}

const MILLISECONDS_PER_SECOND = 1000;

function durationSeconds(route: Route): number | null {
  if (!route.finishTime) {
    return null;
  }
  const ms = Number(route.finishTime) - Number(route.creationTime);
  return Number.isFinite(ms) ? ms / MILLISECONDS_PER_SECOND : null;
}

function failedStepNumber(route: Route): number | null {
  const index = route.steps.findIndex((step) => step.status === 'failed');
  return index === -1 ? null : index + 1;
}

function prependUnderline(str: string) {
  return `_${str}`;
}

function formatTokenAddress(
  eventPayload:
    | { tokenAddress: string | null }
    | { sourceTokenAddress: string | null; destinationTokenAddress: string | null },
) {
  return {
    ...eventPayload,
    ...('tokenAddress' in eventPayload &&
      !!eventPayload.tokenAddress && { tokenAddress: prependUnderline(eventPayload.tokenAddress) }),
    ...('sourceTokenAddress' in eventPayload &&
      !!eventPayload.sourceTokenAddress && {
        sourceTokenAddress: prependUnderline(eventPayload.sourceTokenAddress),
      }),
    ...('destinationTokenAddress' in eventPayload &&
      !!eventPayload.destinationTokenAddress && {
        destinationTokenAddress: prependUnderline(eventPayload.destinationTokenAddress),
      }),
  };
}

/**
 * The widget recreates step objects per event, so they are not reference-equal
 * to `route.steps`. Match on the step's identifying fields to recover its
 * 1-based position in the route.
 */
function stepNumber(route: Route, step: Step): number {
  const index = route.steps.findIndex(
    (candidate) =>
      candidate.fromBlockchain === step.fromBlockchain &&
      candidate.toBlockchain === step.toBlockchain &&
      candidate.fromSymbol === step.fromSymbol &&
      candidate.toSymbol === step.toSymbol &&
      candidate.swapperName === step.swapperName,
  );
  return index === -1 ? 1 : index + 1;
}

/** UI interaction events (token/chain/settings/swap-flow clicks). */
export function handleUiEvent(data: UiEventData): void {
  switch (data.type) {
    case UiEventTypes.SWAP_INITIATED:
      pushToDataLayer(GA4Events.SWAP_INITIATED, toSnakeCaseKeys(formatTokenAddress(data.payload)));
      break;
    case UiEventTypes.SWAP_STARTED:
      pushToDataLayer(GA4Events.SWAP_STARTED, toSnakeCaseKeys(formatTokenAddress(data.payload)));
      break;
    case UiEventTypes.SWAP_WALLETS_MODAL_SHOWN:
      pushToDataLayer(GA4Events.SWAP_WALLETS_MODAL_SHOWN, toSnakeCaseKeys(data.payload));
      break;
    case UiEventTypes.SWAP_WALLETS_CONFIRMED:
      pushToDataLayer(GA4Events.SWAP_WALLETS_CONFIRMED, toSnakeCaseKeys(data.payload));
      break;
    case UiEventTypes.SWAP_RESUMED:
      pushToDataLayer(GA4Events.SWAP_RESUMED, toSnakeCaseKeys(data.payload));
      break;
    case UiEventTypes.SWAP_RETRIED:
      pushToDataLayer(GA4Events.SWAP_RETRIED, toSnakeCaseKeys(data.payload));
      break;
    case UiEventTypes.SWAP_CANCELLED:
      pushToDataLayer(GA4Events.SWAP_CANCELLED, toSnakeCaseKeys(data.payload));
      break;
    case UiEventTypes.TOKEN_SELECTED:
      pushToDataLayer(GA4Events.TOKEN_SELECTED, toSnakeCaseKeys(formatTokenAddress(data.payload)));
      break;
    case UiEventTypes.CHAIN_FILTER_APPLIED:
      pushToDataLayer(GA4Events.CHAIN_FILTER_APPLIED, toSnakeCaseKeys(data.payload));
      break;
    case UiEventTypes.SETTINGS_CHANGED:
      // The widget emits typed values; coerce them to GA4's string form here.
      if (data.payload.setting === 'slippage') {
        pushToDataLayer(GA4Events.SETTINGS_CHANGED, {
          setting_name: 'slippage',
          previous_value: String(data.payload.previousValue),
          new_value: String(data.payload.newValue),
        });
      } else if (data.payload.setting === 'infiniteApproval') {
        pushToDataLayer(GA4Events.SETTINGS_CHANGED, {
          setting_name: 'infinite_approval',
          previous_value: String(data.payload.previousValue),
          new_value: String(data.payload.newValue),
        });
      } else {
        const setting_name =
          data.payload.sourceType === 'bridges' ? 'bridge_exclusion' : 'exchange_exclusion';
        if (data.payload.changeType === 'individual') {
          const { sources } = data.payload;
          pushToDataLayer(GA4Events.SETTINGS_CHANGED, {
            setting_name,
            previous_value: sources
              .map((s) => `${s.id}:${s.enabled ? 'disabled' : 'enabled'}`)
              .join(','),
            new_value: sources
              .map((s) => `${s.id}:${s.enabled ? 'enabled' : 'disabled'}`)
              .join(','),
          });
        } else {
          // Bulk select-all / deselect-all: report the selection state as a summary.
          const { previousSelectedCount, totalCount, changeType } = data.payload;
          let previousSelection = 'some';
          if (previousSelectedCount === 0) {
            previousSelection = 'none';
          } else if (previousSelectedCount === totalCount) {
            previousSelection = 'all';
          }
          pushToDataLayer(GA4Events.SETTINGS_CHANGED, {
            setting_name,
            previous_value: previousSelection,
            new_value: changeType === 'selectAll' ? 'all' : 'none',
          });
        }
      }
      break;
    case UiEventTypes.ROUTE_FEE_VIEWED:
      pushToDataLayer(GA4Events.ROUTE_FEE_VIEWED, toSnakeCaseKeys(data.payload));
      break;
    case UiEventTypes.DESTINATION_ADDRESS_SET:
      pushToDataLayer(GA4Events.DESTINATION_ADDRESS_SET, toSnakeCaseKeys(data.payload));
      break;
    case UiEventTypes.GAS_WARNING_SHOWN:
      pushToDataLayer(GA4Events.GAS_WARNING_SHOWN, toSnakeCaseKeys(data.payload));
      break;
    case UiEventTypes.GAS_WARNING_BYPASSED:
      pushToDataLayer(GA4Events.GAS_WARNING_BYPASSED, toSnakeCaseKeys(data.payload));
      break;
    case UiEventTypes.CLICK_CONNECT_WALLET:
    default:
      // CLICK_CONNECT_WALLET is a preventable click-intent, not a taxonomy event.
      break;
  }
}

/** Quote/route lifecycle events. */
export function handleQuoteEvent(data: QuoteEventData): void {
  switch (data.type) {
    case QuoteEventTypes.ROUTE_REQUESTED:
      pushToDataLayer(GA4Events.ROUTE_REQUESTED, toSnakeCaseKeys(formatTokenAddress(data.payload)));
      break;
    case QuoteEventTypes.ROUTE_NOT_FOUND:
      pushToDataLayer(GA4Events.ROUTE_NOT_FOUND, toSnakeCaseKeys(formatTokenAddress(data.payload)));
      break;
    case QuoteEventTypes.ROUTE_FETCH_FAILED:
      pushToDataLayer(GA4Events.ROUTE_FETCH_FAILED, toSnakeCaseKeys(data.payload));
      break;
    case QuoteEventTypes.ROUTE_CHANGED:
      pushToDataLayer(GA4Events.ROUTE_CHANGED, toSnakeCaseKeys(data.payload));
      break;
    case QuoteEventTypes.QUOTE_INPUT_UPDATE:
    case QuoteEventTypes.QUOTE_OUTPUT_UPDATE:
    default:
      // Internal quote sync events, not part of the GA4 taxonomy.
      break;
  }
}

type WalletConnectEventPayload = Extract<
  WalletEventData,
  {
    type:
      | WalletEventTypes.WALLET_CONNECT_INITIATED
      | WalletEventTypes.WALLET_CONNECTED
      | WalletEventTypes.WALLET_CONNECT_FAILED;
  }
>['payload'];

function createConnectEventParams(payload: WalletConnectEventPayload) {
  return {
    wallet_name: payload.walletName,
    namespace: payload.namespace,
    chain: payload.chain,
    origin: payload.origin,
  };
}

// Custom providers which always get detected
export const WALLET_DETECTED_SKIPPED_IDS = [SOCIAL_WALLET_TYPE];

/**
 * Wallet events, emitted by wallets-react on the widget's `walletEvent` channel.
 * Connect events are per namespace.
 */
export function handleWalletEvent(data: WalletEventData): void {
  switch (data.type) {
    case WalletEventTypes.WALLET_DETECTED:
      if (!WALLET_DETECTED_SKIPPED_IDS.includes(data.payload.walletName)) {
        pushToDataLayer(GA4Events.WALLET_DETECTED, {
          wallet_name: data.payload.walletName,
        });
      }
      break;
    case WalletEventTypes.WALLET_CONNECT_INITIATED:
      pushToDataLayer(GA4Events.WALLET_CONNECT_INITIATED, createConnectEventParams(data.payload));
      break;
    case WalletEventTypes.WALLET_CONNECTED:
      pushToDataLayer(GA4Events.WALLET_CONNECTED, createConnectEventParams(data.payload));
      break;
    case WalletEventTypes.WALLET_CONNECT_FAILED:
      pushToDataLayer(GA4Events.WALLET_CONNECT_FAILED, createConnectEventParams(data.payload));
      break;
    case WalletEventTypes.WALLET_DISCONNECTED:
      pushToDataLayer(GA4Events.WALLET_DISCONNECTED, {
        wallet_name: data.payload.walletName,
        namespace: data.payload.namespace,
      });
      break;
    default:
      break;
  }
}

/**
 * Whole-swap outcome events from the queue manager. `swap_failed` can fire more
 * than once for a single swap (per-step + terminal), so we only emit on the
 * terminal event, identified by `route.status`.
 */
export function handleRouteEvent({ route, event }: RouteEventData): void {
  const firstStep = route.steps[0];
  const lastStep = route.steps[route.steps.length - 1];
  const base = {
    route_id: route.requestId,
    step_count: route.steps.length,
    source_chain: firstStep?.fromBlockchain,
    destination_chain: lastStep?.toBlockchain,
    source_token_symbol: firstStep?.fromSymbol,
    source_token_address:
      firstStep?.fromSymbolAddress && prependUnderline(firstStep.fromSymbolAddress),
    destination_token_symbol: lastStep?.toSymbol,
    destination_token_address:
      lastStep?.toSymbolAddress && prependUnderline(lastStep.toSymbolAddress),
    source_token_amount: toNumberOrNull(route.inputAmount),
    swap_mode: 'swapMode' in event ? event.swapMode : undefined,
  };

  if (event.type === RouteEventType.SUCCEEDED && route.status === 'success') {
    pushToDataLayer(GA4Events.SWAP_COMPLETED, {
      ...base,
      input_amount_usd: toNumberOrNull(event.inputAmountUsd),
      output_amount_usd: toNumberOrNull(event.outputAmountUsd),
      destination_token_amount: toNumberOrNull(event.outputAmount),
      duration_seconds: durationSeconds(route),
    });
  }

  if (event.type === RouteEventType.FAILED && route.status === 'failed') {
    pushToDataLayer(GA4Events.SWAP_FAILED, {
      ...base,
      input_amount_usd: toNumberOrNull(event.inputAmountUsd),
      output_amount_usd: null,
      destination_token_amount: null,
      failed_at_step: failedStepNumber(route),
      failure_reason: mapFailureReason(event.reasonCode),
    });
  }
}

/** Per-step execution outcome events. */
export function handleStepEvent({ route, step, event }: StepEventData): void {
  const base = {
    route_id: route.requestId,
    step_count: route.steps.length,
    step_number: stepNumber(route, step),
    source_chain: step.fromBlockchain,
    destination_chain: step.toBlockchain,
    source_token_symbol: step.fromSymbol,
    source_token_address: step.fromSymbolAddress && prependUnderline(step.fromSymbolAddress),
    destination_token_symbol: step.toSymbol,
    destination_token_address: step.toSymbolAddress && prependUnderline(step.toSymbolAddress),
  };

  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (event.type) {
    case StepEventType.STARTED:
      pushToDataLayer(GA4Events.SWAP_STEP_STARTED, base);
      break;
    case StepEventType.SUCCEEDED:
      pushToDataLayer(GA4Events.SWAP_STEP_SUCCEEDED, base);
      break;
    case StepEventType.FAILED:
      pushToDataLayer(GA4Events.SWAP_STEP_FAILED, {
        ...base,
        failure_reason: mapFailureReason(event.reasonCode),
      });
      break;
    default:
      // Intermediate step events (tx_execution, check_status, ...) are not GA4 events.
      break;
  }
}
